/**
 * GlassButton — pill-shaped tactile button on Liquid Glass material.
 *
 * Supports three intents:
 *   - 'primary'   — lunar-gold filled, maximum prominence
 *   - 'secondary' — translucent glass
 *   - 'ghost'     — minimal, text-only with subtle hover
 *
 * Press animation uses a spring-eased scale-down + shimmer that
 * mimics the iOS 26 button rebound feel.
 */
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';

type Intent = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface GlassButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  intent?: Intent;
  size?: Size;
  fullWidth?: boolean;
  /** Optional leading icon */
  leading?: React.ReactNode;
  /** Optional trailing icon (e.g. arrow → ) */
  trailing?: React.ReactNode;
}

const intentClass: Record<Intent, string> = {
  primary:
    'text-[#0a0a0f] bg-[var(--accent-primary)] border-[var(--accent-primary)] ' +
    'shadow-[0_24px_60px_-24px_rgba(245,217,126,0.5),inset_0_1px_0_0_rgba(255,255,255,0.5)] ' +
    'hover:brightness-110 active:brightness-95',
  secondary:
    'text-[var(--text-primary)] lg-pill ' +
    'hover:bg-[var(--glass-fill-strong)]',
  ghost:
    'text-[var(--text-secondary)] bg-transparent border border-transparent ' +
    'hover:text-[var(--text-primary)] hover:bg-[var(--glass-fill)]',
};

const sizeClass: Record<Size, string> = {
  sm: 'h-9 px-4 text-[13px]',
  md: 'h-12 px-6 text-[15px]',
  lg: 'h-14 px-8 text-[17px]',
};

export const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  function GlassButton(
    {
      intent = 'primary',
      size = 'md',
      fullWidth = false,
      leading,
      trailing,
      className,
      children,
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        className={cn(
          'relative inline-flex items-center justify-center gap-2',
          'font-medium rounded-[var(--radius-pill)]',
          'border outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)]',
          'transition-[transform,filter,background] duration-200 ease-[var(--ease-spring)]',
          'active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed',
          'select-none',
          intentClass[intent],
          sizeClass[size],
          fullWidth && 'w-full',
          className,
        )}
        {...rest}
      >
        {leading && <span className="inline-flex items-center">{leading}</span>}
        <span>{children}</span>
        {trailing && <span className="inline-flex items-center">{trailing}</span>}
      </button>
    );
  },
);
